import ArticleLayout from '@/components/feature/ArticleLayout';

export default function ShippingArticle() {
  return (
    <ArticleLayout
      pageTitle="Kakobuy wysyłka do Polski – koszty, czas i formalności celne 2026 | KakobuyFinds"
      title="Kakobuy wysyłka do Polski – koszty, czas dostawy i formalności celne"
      description="Kompletny przewodnik po wysyłce Kakobuy do Polski – metody dostawy, koszty, czas oczekiwania, cło, VAT i praktyczne wskazówki dla polskich kupujących."
      category="Wysyłka"
      readTime="10 minut czytania"
      breadcrumbs={[
        { label: 'Strona główna', href: '/' },
        { label: 'Wysyłka do Polski' },
      ]}
      faqData={[
        { question: 'Ile kosztuje wysyłka Kakobuy do Polski?', answer: 'Koszty wysyłki do Polski zaczynają się od około 30-50 zł za małe paczki i mogą wynosić 80-150 zł za większe zamówienia. Dokładna cena zależy od wagi, wymiarów i wybranej metody dostawy.' },
        { question: 'Jak długo trwa dostawa Kakobuy do Polski?', answer: 'Standardowy czas dostawy wynosi od 10 do 25 dni roboczych, w zależności od wybranej metody wysyłki. Ekspresowe opcje mogą skrócić ten czas do 5-10 dni.' },
        { question: 'Czy muszę płacić cło za zamówienia z Kakobuy?', answer: 'Przy zamówieniach o wartości poniżej 150 EUR zazwyczaj nie ma opłat celnych, ale może zostać naliczony podatek VAT. Powyżej 150 EUR mogą zostać naliczone dodatkowe opłaty celne.' },
        { question: 'Jakie metody wysyłki oferuje Kakobuy?', answer: 'Kakobuy oferuje kilka metod wysyłki, w tym standardową pocztę lotniczą, przesyłki kurierskie (DHL, FedEx) oraz ekonomiczne opcje morskie. Kakobuy Spreadsheet zawiera orientacyjne informacje o kosztach dla poszczególnych kategorii.' },
      ]}
      articleSchema={{
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'Kakobuy wysyłka do Polski – koszty, czas i formalności celne 2026',
        description: 'Kompletny przewodnik po wysyłce Kakobuy do Polski.',
        author: { '@type': 'Organization', name: 'KakobuyFinds' },
        datePublished: '2026-07-16',
        dateModified: '2026-07-16',
        inLanguage: 'pl',
        publisher: { '@type': 'Organization', name: 'KakobuyFinds' },
      }}
    >
      <section>
        <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4">1. Metody wysyłki Kakobuy do Polski</h2>
        <p className="text-sm md:text-base mb-4">
          <strong>Kakobuy Spreadsheet</strong> nie tylko pomaga znaleźć najlepsze produkty, ale także zawiera informacje o dostępnych metodach wysyłki do Polski. Platforma Kakobuy oferuje kilka opcji dostawy, różniących się ceną i czasem:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
          {[
            { method: 'Poczta lotnicza standardowa', time: '15-25 dni', cost: '30-60 zł', rating: 'Dobry balans' },
            { method: 'Poczta lotnicza priorytetowa', time: '10-18 dni', cost: '50-90 zł', rating: 'Polecana' },
            { method: 'Kurier DHL/FedEx', time: '5-10 dni', cost: '80-150 zł', rating: 'Najszybsza' },
            { method: 'Wysyłka ekonomiczna', time: '20-35 dni', cost: '20-40 zł', rating: 'Najtańsza' },
          ].map((item, idx) => (
            <div key={idx} className="bg-secondary-50 rounded-xl p-4">
              <h4 className="font-semibold text-foreground-800 text-sm mb-2">{item.method}</h4>
              <p className="text-xs text-foreground-500">Czas: {item.time}</p>
              <p className="text-xs text-foreground-500">Koszt: {item.cost}</p>
              <span className="inline-block mt-2 text-xs font-medium text-accent-700 bg-accent-100/60 px-2 py-0.5 rounded-full">{item.rating}</span>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4">2. Koszty wysyłki – co wpływa na cenę?</h2>
        <p className="text-sm md:text-base mb-4">
          Koszt wysyłki z Kakobuy do Polski zależy od kilku czynników. <strong>Kakobuy Spreadsheet</strong> pomaga oszacować te koszty dla poszczególnych kategorii produktów, co ułatwia planowanie budżetu zakupowego.
        </p>
        <ul className="space-y-3 my-4">
          <li className="flex items-start gap-2 text-sm md:text-base">
            <i className="ri-check-line text-accent-500 mt-0.5 shrink-0"></i>
            <span><strong className="text-foreground-800">Waga paczki</strong> – główny czynnik; im cięższa paczka, tym wyższy koszt wysyłki.</span>
          </li>
          <li className="flex items-start gap-2 text-sm md:text-base">
            <i className="ri-check-line text-accent-500 mt-0.5 shrink-0"></i>
            <span><strong className="text-foreground-800">Wymiary paczki</strong> – duże, ale lekkie przedmioty mogą kosztować więcej ze względu na objętość.</span>
          </li>
          <li className="flex items-start gap-2 text-sm md:text-base">
            <i className="ri-check-line text-accent-500 mt-0.5 shrink-0"></i>
            <span><strong className="text-foreground-800">Metoda wysyłki</strong> – kurierzy są drożsi, ale szybsi niż standardowa poczta.</span>
          </li>
          <li className="flex items-start gap-2 text-sm md:text-base">
            <i className="ri-check-line text-accent-500 mt-0.5 shrink-0"></i>
            <span><strong className="text-foreground-800">Ubezpieczenie</strong> – opcjonalne, ale zalecane dla droższych zamówień.</span>
          </li>
        </ul>
      </section>

      <section>
        <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4">3. Formalności celne – cło i VAT w Polsce</h2>
        <p className="text-sm md:text-base mb-4">
          To jeden z najważniejszych tematów dla użytkowników <strong>Kakobuy Spreadsheet</strong> w Polsce. Oto co musisz wiedzieć:
        </p>
        <div className="bg-amber-50 rounded-xl p-5 my-6 border-l-2 border-amber-400">
          <h4 className="font-semibold text-foreground-900 mb-2 flex items-center gap-2">
            <i className="ri-alert-line text-amber-600"></i>
            Kluczowe informacje celne:
          </h4>
          <ul className="space-y-2 text-sm">
            <li className="flex items-start gap-2">
              <span className="text-amber-500 mt-0.5">•</span>
              <span>Zamówienia <strong>do 150 EUR</strong> – zazwyczaj tylko VAT (23% w Polsce), bez cła.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-500 mt-0.5">•</span>
              <span>Zamówienia <strong>powyżej 150 EUR</strong> – VAT (23%) + cło (zależnie od kategorii produktu).</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-500 mt-0.5">•</span>
              <span>Koszt obsługi celnej – dodatkowa opłata pobierana przez przewoźnika (zazwyczaj 10-30 zł).</span>
            </li>
          </ul>
        </div>
        <p className="text-sm md:text-base">
          <strong>Kakobuy Spreadsheet</strong> zawiera orientacyjne wartości produktów, co pomaga w planowaniu budżetu z uwzględnieniem potencjalnych opłat celnych.
        </p>
      </section>

      <section>
        <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4">4. Jak śledzić przesyłkę?</h2>
        <p className="text-sm md:text-base mb-4">
          Po złożeniu zamówienia przez linki z <strong>Kakobuy Spreadsheet</strong>, sprzedawca nada przesyłkę i udostępni numer śledzenia. Oto jak monitorować dostawę:
        </p>
        <ol className="space-y-3 my-4">
          <li className="flex items-start gap-3 text-sm">
            <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-xs font-medium shrink-0">1</span>
            <span>Sprawdź numer śledzenia w panelu zamówień Kakobuy</span>
          </li>
          <li className="flex items-start gap-3 text-sm">
            <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-xs font-medium shrink-0">2</span>
            <span>Użyj uniwersalnych narzędzi jak 17TRACK lub Postal Ninja</span>
          </li>
          <li className="flex items-start gap-3 text-sm">
            <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-xs font-medium shrink-0">3</span>
            <span>Po dotarciu do Polski śledź przez Pocztę Polską lub stronę kuriera</span>
          </li>
        </ol>
      </section>

      <section>
        <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4">5. Praktyczne wskazówki</h2>
        <div className="space-y-3 my-6">
          {[
            'Łącz kilka małych zamówień w jedną paczkę, aby zaoszczędzić na wysyłce.',
            'Zawsze wykupuj ubezpieczenie dla zamówień powyżej 200 zł.',
            'Zapisuj numery śledzenia – przydadzą się w przypadku opóźnień.',
            'Sprawdzaj szacunkowe koszty wysyłki w Kakobuy Spreadsheet przed zakupem.',
            'Zamawiaj z wyprzedzeniem – dostawa może potrwać nawet miesiąc.',
          ].map((tip, idx) => (
            <div key={idx} className="flex items-start gap-2 p-3 bg-secondary-50 rounded-xl text-sm">
              <span className="text-accent-500 mt-0.5 shrink-0">💡</span>
              <span className="text-foreground-700">{tip}</span>
            </div>
          ))}
        </div>
      </section>
    </ArticleLayout>
  );
}